/**
 * Centralized user-facing copy module — SRS §1.3 / FR-AI-3.
 *
 * Compliance vocabulary: this module is the single source of truth for every
 * string the user sees. Disallowed terms ("diagnose", "cure", "treat", "disease",
 * "patient") are blocked by the vocabularyAudit() check below, which CI runs
 * on every commit.
 */

import type { Goal } from './types.js';

const DISALLOWED_TERMS = ['diagnose', 'cure', 'treat', 'disease', 'patient'] as const;

export const goalCopy: Record<Goal, { title: string; tagline: string }> = {
  stress: { title: 'Stress support', tagline: 'Calm the nervous system' },
  sleep: { title: 'Sleep optimization', tagline: 'Drift into deep rest' },
  focus: { title: 'Focus', tagline: 'Sharpen attention' },
  meditation: { title: 'Meditation', tagline: 'Settle into stillness' },
  'emotional-balance': { title: 'Emotional balance', tagline: 'Coherence through the day' },
  grounding: { title: 'Grounding', tagline: 'Reconnect to the body' },
  recovery: { title: 'Recovery', tagline: 'Restore between sessions' },
  energy: { title: 'Energy', tagline: 'Light up the system' },
  abundance: { title: 'Abundance mindset', tagline: 'Open to possibility' },
  radiance: { title: 'Radiance', tagline: 'Soften and glow' },
};

// Naming rule: the product is "Qi Scan" in all body / sentence copy. The
// QiCoil parent wordmark (logo image) stays in the Wordmark component, and the
// page <title> is "QiScan.Ai".
export const disclaimer = {
  short: 'Qi Scan supports wellness optimization. It is not a medical device.',
  long: 'Qi Scan is designed to support wellness optimization, stress patterns, emotional coherence, and recovery. It does not provide medical advice or replace medical professionals.',
} as const;

export const navigation = {
  dashboard: 'Dashboard',
  scan: 'New scan',
  history: 'History',
  sessions: 'Sessions',
  player: 'Rebalance',
  settings: 'Settings',
  signIn: 'Sign in',
  signOut: 'Sign out',
  upgrade: 'Upgrade',
} as const;

export const onboarding = {
  welcome: 'Welcome to Qi Scan',
  permissionsTitle: 'Let your senses lead',
  permissionsBody:
    'We use your camera and microphone to measure resonance signals. Everything stays on your device unless you choose to share.',
  cameraTitle: 'Allow camera',
  cameraBody: 'Used for blink, breath, and facial stability — never recorded.',
  microphoneTitle: 'Allow microphone',
  microphoneBody: 'Captures a brief guided phrase for voice analysis.',
  goalPickerTitle: 'What would you like to optimize?',
  goalPickerBody: 'Pick one — we will tune your protocol around it.',
} as const;

export const scan = {
  bioscanCue: 'Sit still in a quiet space. Relax.',
  voiceCue: 'When you are ready, speak this phrase aloud.',
  voicePhrase: [
    'I am settled, alert, and open to what comes next.',
    'With each slow breath, I feel calm, clear, and steady.',
  ],
  sweepReadyTitle: 'Turn your volume up',
  sweepReadyBody:
    'This step plays soft tones. Set your volume to a comfortable, normal level before you begin.',
  sweepCue: 'Notice how each tone lands. There is no wrong response.',
  resultsCue: 'Your resonance map',
} as const;

export const session = {
  modes: {
    ambient: 'Ambient',
    'pulsed-harmonic': 'Pulsed harmonic',
    binaural: 'Binaural-style',
  },
  startingIn: 'Starting in',
  ending: 'Closing the session',
  rate: 'How did this feel?',
} as const;

/**
 * Walks every string in this module and throws if a disallowed compliance term
 * appears. Called by a Vitest spec and the CI lint workflow.
 */
export function vocabularyAudit(input: Record<string, unknown> = allCopy()): string[] {
  const violations: string[] = [];
  const visit = (value: unknown, path: string): void => {
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      for (const term of DISALLOWED_TERMS) {
        if (lower.includes(term)) {
          violations.push(`${path}: contains disallowed term "${term}"`);
        }
      }
    } else if (value && typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) {
        visit(v, path ? `${path}.${k}` : k);
      }
    }
  };
  visit(input, '');
  return violations;
}

export function allCopy(): Record<string, unknown> {
  return { goalCopy, disclaimer, navigation, onboarding, scan, session };
}
